import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatDrawer } from '@angular/material/sidenav';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { DrawingService, FeatureSelectService, LayerStylesService, MapService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { PERMISSION_NAME, PermissionService, SplitViewService, UtilsService } from '@ebw-monorepo/utils';
import { MtxGridColumn, MtxGridRowClassFormatter } from '@ng-matero/extensions/grid';
import { safeJsonParse, safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { toStringXY } from 'ol/coordinate';
import { extend } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { firstValueFrom, Subject, Subscription } from 'rxjs';

import { LoginService } from '@ebw-monorepo/login';
import { API_URLS, ApiUrlService } from '../../../../../utils/src/lib/services/api-url.service';
import { MapRequestService } from '../../services/map-request.service';
import { IHttpRequest } from './../../interfaces/httpReq.interface';
import { IGroupButton, IMapTable, IMapTableData, IResponse } from './../../interfaces/mapTable.interface';
import { MapTableService } from './../../services/map-table.service';
import { AttachmentDialogComponent } from './attachment-dialog/attachment-dialog.component';
import { ExportService } from '../../services/export.service';

@Component({
  selector: 'ebw-monorepo-map-table',
  templateUrl: './map-table.component.html',
  styleUrls: ['./map-table.component.scss']
})
export class MapTableComponent implements OnInit, OnDestroy {
  @ViewChild('geometryColTemplate', { static: true }) geometryColTemplate!: TemplateRef<any>;
  @ViewChild('infoColTemplate', { static: true }) infoColTemplate!: TemplateRef<any>;
  @ViewChild('pinnableRowColTemplate', { static: true }) pinnableRowColTemplate!: TemplateRef<any>;

  @ViewChild('drawer', { static: true }) detailDrawer!: MatDrawer;

  @Input() groupButtons: IGroupButton[] = [];
  @Input() title = 'map.map_table.result';
  // valorizzato dopo risposta otdr, messo in input cosi puo essere usato in altri contesti
  @Input() subtitle = '';

  onDestroy$: Subject<void> = new Subject();

  public dataGrid;
  public columnsGrid: MtxGridColumn[] = [];
  public currentPage = 0;
  public dataSize = 26;
  public paginationLimitSize = 25;
  public tableLimitSize = 25;
  public levels = 1;
  public pageSizeOptions = [10, 25, 50];
  public discoveredDataSize = false;
  public details: { title: string; data: any[]; index: number; properties?: any }[] = [];
  public currentDetailIndex: number;
  public currentTab: IMapTableData = null;
  public rowClassFormatter: MtxGridRowClassFormatter = {
    pinned: (data, index) => this.isSelected(data.id)
  };
  public isValve: boolean = false;
  public isQuery: boolean = false;
  public canExportAdvanced: boolean = false;

  /**default per i groupButtons */
  selectedButton = '';
  config: IMapTable;
  private subscription: Subscription[] = [];
  private mapData: IMapTableData[];
  private _highlightStyle;
  private rowSelectedMap: Map<string, unknown> = new Map();

  constructor(
    private mapTableSrv: MapTableService,
    private mapService: MapService,
    private httpClient: HttpClient,
    private ebwModal: EbwModalService,
    private i18NSrv: I18NService,
    private requestSrv: MapRequestService,
    private _featureSelectService: FeatureSelectService,
    private _layerStylesService: LayerStylesService,
    private _dialog: MatDialog,
    private _splitViewService: SplitViewService,
    private _utilsService: UtilsService,
    private _drawingService: DrawingService,
    private _apiUrlService: ApiUrlService,
    private _exportService: ExportService,
    private _permissionService: PermissionService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.canExportAdvanced = this._permissionService.checkPermission(PERMISSION_NAME.MAP.ADVANCED_QUERY_EXPORT);
    // called when a query is executed
    const sub = this.mapTableSrv.getMapTableConfig().subscribe({
      next: (resp) => {
        if (resp) {
          // check that the table is representing the result of a query
          const title = resp.title.split('.').slice(-1)[0];
          if (title === 'results_query') {
            this.isQuery = true;
          }
          this.mapService.clearAllFeaturesFromTemporaryLayer();
          this.selectedButton = '';
          this.groupButtons = [];
          this.dataGrid = [];
          this.columnsGrid = [];
          // store the config from the response
          this.config = resp;
          this.title = resp.title;

          if (this.config.queryGeometry) {
            setTimeout(() => {
              this.mapService.centerMapOnGeometry(this.config.queryGeometry);
            }, 0);
          }

          if (this.config.groupButtons) {
            this.groupButtons = this.config.groupButtons;
            this.selectedButton = this.groupButtons[0].name;
          }

          // notify that the data size hasn't been discovered yet
          this.discoveredDataSize = false;
          // clear the component
          this._clearPagination();
          // get the data from the query
          this._getDataFn(this.paginationLimitSize);
        }
      },
      error: (err) => {
        console.error(err);
      }
    });

    this.subscription.push(sub);
    this._highlightStyle = this._layerStylesService.buildStyleFromFeatureStyle(this._drawingService.highlightFeatureStyle());
  }

  // -------------------------------------------------------------------------------------------------------------------

  ngOnDestroy() {
    this.closeMapTable();
  }

  // --------------------------------------------------------------------------------------------------------------------

  closeMapTable() {
    this.mapTableSrv.setMapTableConfig(null);
    this.mapService.clearAllFeaturesFromTemporaryLayer();
    this.mapService.clearAllFeaturesFromTemporaryLayer(true);
    this.subscription.map((s: Subscription) => s.unsubscribe());
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * function to execute a query and obtain its data
   * @param {number} limit max number of entries to retrieve
   * @param {number} offset starting point for the entries
   */
  private _getDataFn(limit: number = 25, offset: number = 1) {
    // setup the required headers
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
    });

    const opts = {
      headers: headers
    };
    let url = this.config?.url;
    /**non tutte le url gestiscono la paginazione, quindi controllo se è da gestire tramite il flag impostato in configurazione */
    if (this.config.pagination) {
      url += `?id=${this.config.id}&limit=${limit}&offset=${offset}`;
    }

    // if the config contains a count field, make a request with the count parameter to get the number of elements in the result of the query
    if (this.config.count) {
      // disable counting (only needs to be done once)
      this.config.count = false;
      // add the parameter to the HTTP request
      const countUrl = url + `&count=true`;

      // make the request and extract the count
      this.httpClient.get(countUrl, opts).subscribe({
        next: (resp: IResponse) => {
          this.dataSize = resp[0].count;
          this.discoveredDataSize = true;
        },
        error: (err) => {
          this.ebwModal.openErrorModal({
            title: this.i18NSrv.getInstantValue('common.error'),
            content: err.message
          });
        }
      });
    }

    // make the http request to execute the query and retrieve the records
    this.httpClient.get(url, opts).subscribe({
      next: (resp: IMapTableData[]) => {
        if (resp && resp[0].data.length == 0 && this.currentPage == 0) {
          this.dataGrid = [];
          this.columnsGrid = [];
          this.rowSelectedMap = new Map();
          // return; //! FN. commentati i return perchè se primo tab è vuoto il componente non mostra nulla (nemmeno gli altri tab)
        }
        // if there is no data available, it means we surpassed the limit of the query, so we also found the amount of data contained
        if (resp && resp[0].data.length == 0 && !this.discoveredDataSize) {
          this.dataSize = this.currentPage * this.paginationLimitSize;
          this.currentPage -= 1;
          this.discoveredDataSize = true;
          // return; //! FN. commentati i return perchè se primo tab è vuoto il componente non mostra nulla (nemmeno gli altri tab)
        }
        // clear the map from the previous temporary layer
        // this.mapService.clearAllFeaturesFromTemporaryLayer(true);
        // store the data received
        this.mapData = resp;

        /** se i buttonsGroup non sono stati passati in input, me li recupero dalla chiamata */
        this.groupButtons = this.mapTableSrv.createMapButtonsGroup(this.mapData);
        this.selectedButton = this.groupButtons[0].label;

        /**Aggiungo in automatico layers su mappa se ho showGeometries = true & hide = true */
        let defaultLayers = this.mapData.filter((m) => m.showGeometries);
        const format = new GeoJSON();
        if (defaultLayers && defaultLayers.length > 0) {
          this.mapService.clearAllFeaturesFromTemporaryLayer();
          let layerExtent = null;
          defaultLayers.forEach((d, i) => {
            //const index = 0;
            d.data.map((el, y) => {
              let featureStyle = null;
              if (el.properties.style) {
                if (el.properties.style.includes('wa_gate_valve') && el.geometry.type == 'Point') {
                  featureStyle = this._layerStylesService.buildFeatureStyleFromId('impianti', {
                    title: `${y + 1}`
                  });
                }
                if (el.properties.style.includes('wa_service') && el.geometry.type == 'Point') {
                  featureStyle = this._layerStylesService.buildFeatureStyleFromId('utenze', {
                    title: `${y + 1}`
                  });
                }
              }
              if (el.properties.round == 'secondary' && el.geometry.type == 'LineString') {
                featureStyle = this._layerStylesService.buildFeatureStyleFromId('secondary');
              }

              /**aggiungo layer temporaneo per ogni riga della tabella */
              this.mapService.addFeatureToTemporaryLayer(el.geometry, featureStyle, false, false, null, `${y + 1}`);

              const options = {
                dataProjection: MapService.EPSG_3857_KEY,
                featureProjection: MapService.EPSG_3857_KEY
              };
              const geomExtent = format.readGeometry(el.geometry, options).getExtent();

              if (!layerExtent) {
                layerExtent = geomExtent;
              }
              layerExtent = extend(layerExtent, geomExtent);
            });
          });
          // prepare a default size in case the map dimensions are 0
          const size = this.mapService.openLayersMap.getSize();

          if (size[0] === 0) {
            size[0] = 500;
          }
          if (size[1] === 0) {
            size[1] = 500;
          }

          this.mapService.openLayersMap.getView().fit(layerExtent, { size: size });
        }
        // initialize the table with the new data
        this.initializeTable();
      },
      error: (err) => {
        this.ebwModal.openErrorModal({
          title: this.i18NSrv.getInstantValue('common.error'),
          content: err?.error?.message ?? this.i18NSrv.getInstantValue('common.data_retrieving_error')
        });
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  centerMap(geometry: any) {
    this.mapService.addFeatureToTemporaryLayer(geometry, null);
    setTimeout(() => {
      this.mapService.centerMapOnGeometry(geometry, false, true);
    }, 0);
  }

  // ------------------------------------------------------------------------------------------------------------

  public onCenterMapPlaceRequested(event: MouseEvent, element, forced = false) {
    event.stopPropagation();
    this.centerMapPlace(element, forced);
  }

  /**
   *
   * @param element singolo elemento della tabella
   * @param forced parametro che indica se interrompere forzatamente la procedura dopo 1 ciclo
   * @returns void
   */
  async centerMapPlace(element: any, forced = false) {
    // Se esiste la geometria centro la mappa subito sulla geometria
    if (element.geometry) {
      this.mapService.clearAllFeaturesFromTemporaryLayer();
      this._updateSplitViewConf({ halfScreen: true, collapsed: false });
      this.centerMap(element.geometry);
      return;
    }
    // se true esco
    if (forced) {
      return;
    }
    // se non ho la geometria nell'oggetto cerco di ricavarmela attraverso il suo URN (se presente)
    const elementWithGeometry = await this.getElementGeometry(element);
    if (elementWithGeometry) {
      this.centerMapPlace(elementWithGeometry, true);
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  async centerMapMarker(event, element: any, index: string, forced = false) {
    if (element.geometry) {
      const titleMarker = index.toString();
      if (event.target.checked) {
        this._updateSplitViewConf({ halfScreen: true, collapsed: false });
        //uso l'index com id e titlo della geometria
        setTimeout(() => {
          this.mapService.centerMapOnGeometry(element.geometry);
        }, 0);
        this.mapService.addMarkerToTemporaryLayer(titleMarker, element.geometry.coordinates, MapService.EPSG_3857_KEY);
      } else {
        this.mapService.removeMarkerFromTemporaryLayer(titleMarker);
      }
      return;
    }
    // se true esco
    if (forced) {
      return;
    }
    // se non ho la geometria nell'oggetto cerco di ricavarmela attraverso il suo URN (se presente)
    const elementWithGeometry = await this.getElementGeometry(element);
    if (elementWithGeometry) {
      this.centerMapMarker(event, elementWithGeometry, index, true);
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  async getElementGeometry(element) {
    try {
      // Cerco ricavarmi l'URN cercando se ci sono proprietá con una chiave simile
      const key = Object.keys(element).find((key) => key.endsWith('urn') || key.endsWith('urns'));
      const urn = element[key];
      // se urn non esiste esco
      if (!urn) {
        throw Error();
      }
      // recupero la geometria tramite urn via API
      const response = await this._featureSelectService.selectByUrns([urn]);
      // se errore esco
      if (response.error) {
        throw Error();
      }
      // Estraggo feature in questione
      const feature = response.result.features[0];
      if (!feature.geometry) {
        // cerco di ereditare le geometrie da asset contenitori
        const inheritedGeometry = await this._featureSelectService.getPlacedAssetGeometry(feature);
        // se non riesco ad ottenerla ritorno oggetto al componente perchè si aggiorni internamente
        if (inheritedGeometry) {
          feature.geometry = inheritedGeometry;
        }
      }
      if (feature.geometry) {
        const format = new GeoJSON();
        const readOptions = {
          dataProjection: MapService.EPSG_4326_KEY,
          featureProjection: MapService.EPSG_3857_KEY
        };
        // Leggo geometria nel sistema di riferimento corretto
        const geomR = format.readGeometry(feature.geometry, readOptions);
        const writeOptions = {
          dataProjection: MapService.EPSG_3857_KEY,
          featureProjection: MapService.EPSG_3857_KEY
        };
        // la creo nel formato che vuole OpenLayer
        const geomW = format.writeGeometry(geomR, writeOptions);
        // Ricostruisco oggetto che vuole il metodo centerMapPlace
        return { geometry: JSON.parse(geomW) };
      }
      return false;
    } catch (error) {
      console.error('[MapTableComponent][getElementGeometry]', error);
      this.ebwModal.openErrorModal({
        title: 'Dato non disponibile',
        content: 'Non é stato possibile recuperare la geometria di questo oggetto'
      });
      return false;
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  getInitTableFromName(event) {
    /**Aggiorno il valore selezionato */
    this.selectedButton = event.value;

    /**aggiorno la lista */
    this.initializeTable(true);

    /* rimuovo i marker correnti */
    this.mapService.removeAllMarkerFromTemporaryLayer();
  }

  /**
   * function to initialize the table
   * @param fromTabChange
   * @returns
   */
  initializeTable(fromTabChange: boolean = false) {
    this.currentTab = this.mapData.find((t) => t.tab_name == this.selectedButton);
    if (!this.currentTab) {
      this.ebwModal.openErrorModal({
        title: this.i18NSrv.getInstantValue('common.error'),
        content: this.i18NSrv.getInstantValue('common.data_retrieving_error')
      });
      return;
    }

    let tempDataGrid: any[] = [];
    let tempColumnsGrid: MtxGridColumn[] = [];

    if (this.currentTab.data.length > 0) {
      // query to gss database
      if (this.currentTab.data[0].id && /Z[0-9][0-9]*$/.test(this.currentTab.data[0].id)) {
        const { config_collection } = this._featureSelectService.getConfigDataFromUrn(this.currentTab.data[0].id);
        if (config_collection) {
          tempColumnsGrid = config_collection.fields
            .filter((_) => _.name !== 'ww_attachments_path' && _.name.toLowerCase() !== 'id')
            .map((field) => {
              const column: MtxGridColumn = {
                header: field.external_name,
                field: field.name.toLowerCase(),
                minWidth: 180,
                type: field.type
              };

              switch (field.type) {
                case 'attachments':
                  // column.pinned = 'right';
                  column.type = 'button';
                  column.buttons = [
                    {
                      type: 'icon',
                      text: '',
                      icon: 'preview',
                      tooltip: 'Visualizza allegati',
                      color: 'accent',
                      iif: (rowData) => {
                        return rowData.attachments ? rowData.attachments.length > 0 : false;
                      },
                      click: (rowData) => {
                        this.openAttachmentDialog(rowData.attachments.map((x) => safeJsonParse(x)).filter((attachment) => !!attachment));
                      }
                    }
                  ];

                  break;

                default:
                  break;
              }

              return column;
            });
        } else {
          throw new Error('Collection non trovata. Non so come rappresentare il dato nella tabella');
          // //TODO: capire se ci sono casistiche in cui passa in questo ramo
          // const fakeGeoJSON = {
          //   type: 'FeatureCollection',
          //   features: this.currentTab.data
          // };

          // new GeoJSON().readFeatures(fakeGeoJSON, {}).forEach((feature, index) => {
          //   let rowData = this._featureSelectService.getTranslatedFeatureProperty(feature);
          //   const geometry = this.currentTab.data[index].geometry;

          //   if (geometry) {
          //     rowData['geometry'] = geometry;
          //   }

          //   tempDataGrid.push(rowData);
          // });

          // Object.keys(tempDataGrid[0]).forEach((key, index) => {
          //   switch (key) {
          //     // case "Feature":
          //     //   this._addInfoColumn(tempColumnsGrid);
          //     //   break;
          //     // case "geometry":
          //     //   this._addGeometryColumn(tempColumnsGrid);
          //     //   break;
          //     default:
          //       tempColumnsGrid.push({ header: key, field: key, minWidth: 140 });
          //       break;
          //   }
          // });
        }
      }
      // query to postgres database
      else {
        for (const [key, value] of Object.entries(this.currentTab.data[0].properties)) {
          const column: MtxGridColumn = {
            header: key,
            field: key,
            minWidth: 180
          };
          // TODO: aggiunto per sviluppo TELCO, dopo aver definito una strategia preferibile evitarlo utilizzando le config su db
          // per non mostrare prop 'urn' ma renderla disponibile per la selectByUrn per recuperare la geometria dell'oggetto nel caso non ci fosse ma fosse recuperabile
          if (key !== 'urn') {
            tempColumnsGrid.push(column);
          }
        }
      }

      // TODO questo if potrebbe essere necessario commentarlo perchè il tab geometry ci deve essere sempre
      if (this.currentTab.data[0].geometry) {
        this._addGeometryColumn(tempColumnsGrid);
      }

      if (this.currentTab.data[0].type == 'Feature' && this.config.infoColumn) {
        this._addInfoColumn(tempColumnsGrid);
      }

      if (this.config.pinnableRow) {
        this._addPinnableColumn(tempColumnsGrid);
      }

      const tmp =
        tempDataGrid.length > 0
          ? tempDataGrid
          : this.currentTab.data.map((item) => ({
              geometry: item.geometry,
              xy: item.geometry && item.geometry.type === 'Point' ? toStringXY(item.geometry.coordinates, 1) : null,
              pinned: false,
              ...item.properties,
              id: item.id ?? item.properties.id
            }));

      //se ho delle row selezionate le mantengo nel mio set di dati, controllo e controllo che non ci siano duplicati
      let filtredData = [];
      if (this.rowSelectedMap.size > 0) {
        filtredData = [...this.rowSelectedMap.values()];
        tmp.forEach((item) => {
          if (item.hasOwnProperty('id') && !this.rowSelectedMap.has(item.id)) {
            filtredData.push(item);
          }
        });
      } else {
        filtredData = tmp;
      }

      this.tableLimitSize = filtredData.length;

      setTimeout(() => {
        this.dataGrid = filtredData;
        this.columnsGrid = tempColumnsGrid;

        //preparo l'array che terrà in memoria i dettagli richiesti
        const n = this.dataGrid.length;
        this.details = new Array(n).fill(null);
        this.currentDetailIndex = null;
      }, 0);
    } else if (fromTabChange) {
      this.dataGrid = [];
      this.columnsGrid = [];

      //TODO: valutare info box "non ci sono altri risultati"
      // this.ebwModal.openErrorModal({
      //   title: this.i18NSrv.getInstantValue('common.error'),
      //   content: this.i18NSrv.getInstantValue('common.no_result')
      // });
    }
    if (this.paginationLimitSize > this.currentTab.data.length && !this.discoveredDataSize) {
      // se ricevo meno dati di quanto richiesto significa che non ne ho altri e quindi disabilito la paginazione
      this.dataSize = this.currentPage * this.paginationLimitSize + this.currentTab.data.length;
      this.discoveredDataSize = true;
    }
  }

  /**
   * Method called when pressing a dropdown option in the simple export menu
   *
   * @param {'excel' | 'csv' | 'pdf'} type String defining the format of the export to perform
   */
  export(type: 'excel' | 'csv' | 'pdf'): void {
    let url = null;
    let body = null;
    let blobType = 'text/plain';

    // elimino la proprietá 'urn' da ogni elemento per non farla apparire nei file di export
    const activeTab: IMapTableData = safeJsonClone(this.currentTab);

    // se non ho elementi esco
    if (!activeTab?.data?.length) {
      this.ebwModal.openInfoModal({
        title: this.i18NSrv.getInstantValue('map.map_table.no_data_to_export'),
        content: ''
      });
      return;
    }

    // altrimenti
    activeTab.data = activeTab.data.map((d) => {
      delete d.properties.urn;
      return d;
    });

    // format the data and set the response type depending on the export format
    switch (type) {
      case 'excel':
        url = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.EXCEL) + '?filename=' + this.selectedButton;
        body = { data: this.mapTableSrv.formatExportData(this.currentTab, false, true) };
        blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
      case 'csv':
        url = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.CSV) + '?filename=' + this.selectedButton;
        body = { data: this.mapTableSrv.formatExportData(this.currentTab) };
        blobType = 'text/csv';
        break;
      case 'pdf':
        url = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.PDF) + '?filename=' + this.selectedButton;
        body = { data: this.mapTableSrv.formatExportData(this.currentTab, true) };
        blobType = 'application/pdf';
        break;
    }

    // if everything was set correctly, make the http request to the export endpoint
    if (url && body) {
      const req: IHttpRequest = {
        url: url
      };
      const opt = {
        responseType: 'blob'
      };
      this.requestSrv.postMapRequest(req, body, opt).subscribe({
        next: (data: any) => {
          // if the response is correct, download the file
          let downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(new Blob([data], { type: blobType }));
          downloadLink.setAttribute('download', this.selectedButton);
          document.body.appendChild(downloadLink);
          downloadLink.click();
        }
      });
    }
  }

  /**
   * Method for opening the query export menu
   */
  public onQueryExport(): void {
    // make a copy of the active tab, in case the user wants to only export the current tab
    const activeTab: IMapTableData = safeJsonClone(this.currentTab);

    // if the tab has any element
    if (!activeTab?.data?.length) {
      // open an information modal telling the user that there's no data to export
      this.ebwModal.openInfoModal({
        title: this.i18NSrv.getInstantValue('map.map_table.no_data_to_export'),
        content: ''
      });
      return;
    }

    // remove the urn from each entry
    activeTab.data = activeTab.data.map((d) => {
      delete d.properties.urn;
      return d;
    });

    activeTab.name = this.config.queryName;

    // open the export dialog with the current data and the query information
    this._exportService.openExportDialog(activeTab, { queryId: this.config.id, queryName: this.config.queryName });
  }

  increasesLevel(type: 'prev' | 'next') {
    if (type == 'prev') {
      if (this.levels == 2) {
        this.levels--;
        this.config.url = this.config.url.replace('secondary', 'primary');
        this._getDataFn();
      }
    } else {
      if (this.levels == 1) {
        this.levels++;
        this.config.url = this.config.url.replace('primary', 'secondary');
        this._getDataFn();
      }
    }
  }

  onPagination(event: PageEvent) {
    let offset = 1;
    this.paginationLimitSize = event.pageSize;
    this.tableLimitSize = event.pageSize;

    offset = event.pageIndex * this.paginationLimitSize + 1;

    if (!this.discoveredDataSize) {
      this.dataSize = this.paginationLimitSize + offset;
    }

    this.currentPage = event.pageIndex;
    this._getDataFn(this.paginationLimitSize, offset);
  }

  openAttachmentDialog(attachments): void {
    this._dialog.open(AttachmentDialogComponent, {
      hasBackdrop: true,
      width: '60vw',
      data: attachments
    });
  }

  public onDetailRequestFromDrawer(index: number) {
    const rowDataRequested = this.dataGrid[index];
    this._viewDetail(rowDataRequested.id, index);
  }

  public onDetailRequest(event: MouseEvent, urn: string, index: number) {
    event.stopPropagation();
    this._viewDetail(urn, index);
  }

  public onPinChecked(checked, rowData, index) {
    //rowData.pinned = checked;

    if (!this.rowSelectedMap.has(rowData.id)) {
      this.rowSelectedMap.set(rowData.id, rowData);
    } else {
      this.rowSelectedMap.delete(rowData.id);
    }
    this.hightlightPinnedItem();
  }

  public selectDeselectAll(checked) {
    this.dataGrid.forEach((item) => {
      if (!checked && this.isSelected(item.id)) {
        this.rowSelectedMap.delete(item.id);
      } else {
        this.rowSelectedMap.set(item.id, item);
      }
    });

    this.hightlightPinnedItem();
  }

  public isAllSelected(): boolean {
    return this.rowSelectedMap.size === this.dataGrid.length;
  }

  public isSelected(id): boolean {
    return this.rowSelectedMap.has(id);
  }
  public hightlightPinnedItem() {
    this.mapService.clearFeatureFromTemporaryLayerById('highlight_feature_', false, true);

    if (this.rowSelectedMap.size > 0) {
      this.rowSelectedMap.forEach((value: any, key) => {
        if (value.geometry) {
          this.mapService.addFeatureToTemporaryLayer(
            value.geometry,
            this._highlightStyle,
            false,
            false,
            // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
            `highlight_feature_${Math.random()}`
          );
        }
      });

      this.mapService.centerMapOnTempLayer();
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  private _clearPagination() {
    this.dataSize = this.paginationLimitSize + 1;
    this.currentPage = 0;
    this.rowSelectedMap = new Map();
  }

  private _updateSplitViewConf(layoutConf) {
    let currentLayoutConf = this._splitViewService.viewLayountConfig$.value;
    if (layoutConf && currentLayoutConf.isEnable) {
      currentLayoutConf = Object.assign({}, currentLayoutConf, layoutConf);
      this._splitViewService.layoutConfChange(currentLayoutConf);
    }
  }

  private async _viewDetail(urn: string, index: number) {
    const url = this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN);
    let response = await firstValueFrom(this._utilsService.postRequest(url, { urns: urn }, false));
    let feature = new GeoJSON().readFeatures(response.result)[0];
    await this._featureSelectService.prepareFeatureObject(feature, urn);
    await this._featureSelectService.mapFeatureToModal(feature);

    const featureDetails = feature['details'] ?? feature?.getProperties()?.details;

    const mappedFeaure = this._featureSelectService.parseFeature(featureDetails);
    const data = this._featureSelectService.objectToArray(mappedFeaure);

    const currentDetail = {
      title: featureDetails?.title,
      data,
      properties: feature.getProperties(),
      index
    };

    this.details[index] = currentDetail;
    this.currentDetailIndex = index;
    this.detailDrawer.open();

    this.centerMapPlace(feature, false);
  }

  private _addGeometryColumn(tableGrid: MtxGridColumn[]) {
    const geometryCellDef: MtxGridColumn = {
      header: 'Geometria',
      field: 'geometry',
      width: '30px',
      pinned: 'left',
      cellTemplate: this.geometryColTemplate
    };

    tableGrid.unshift({ header: 'XY', field: 'xy', width: '180px' });
    tableGrid.unshift(geometryCellDef);
  }

  private _addInfoColumn(tableGrid: MtxGridColumn[]) {
    const infoColumns: MtxGridColumn = {
      header: 'Info',
      field: 'id',
      width: '30px',
      pinned: 'left',
      cellTemplate: this.infoColTemplate
    };

    tableGrid.unshift(infoColumns);
  }

  private _addPinnableColumn(tableGrid: MtxGridColumn[]) {
    const pinColumns: MtxGridColumn = {
      header: 'Pin',
      field: 'selected',
      width: '30px',
      pinned: 'left',
      cellTemplate: this.pinnableRowColTemplate
    };

    tableGrid.unshift(pinColumns);
  }
}
